import { DotsThreeVerticalIcon } from "@phosphor-icons/react/dist/csr/DotsThreeVertical";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import {
  ACTIVE_REFERRAL_STATUSES,
  REFERRAL_STATUS,
} from "@/referrals/constants";
import { IReferral } from "@/referrals/referral";
import {
  ReferralConfirmationModal,
  type ReferralConfirmAction,
} from "./referral-confirmation-modal";

interface ReferralOptionsDropdownProps {
  referral: IReferral;
  onLost?: () => void;
  onMoveToPaid?: () => void;
  onDelete?: () => void;
  className?: string;
}

export const ReferralOptionsDropdown = ({
  referral,
  onLost,
  onMoveToPaid,
  onDelete,
  className,
}: ReferralOptionsDropdownProps) => {
  const [confirmAction, setConfirmAction] =
    useState<ReferralConfirmAction | null>(null);

  const hasLostOption =
    !!onLost &&
    ACTIVE_REFERRAL_STATUSES.filter(
      (status) => status !== REFERRAL_STATUS.CLOSED,
    ).includes(referral.status);

  const hasMoveToPaidOption =
    referral.payouts.length > 0 &&
    !!onMoveToPaid &&
    referral.status !== REFERRAL_STATUS.PAID;

  const hasDropdownOptions = hasLostOption || hasMoveToPaidOption || !!onDelete;

  if (!hasDropdownOptions) {
    return null;
  }

  const handleSelect =
    (action: () => void) => (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      action();
    };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              type="button"
              aria-label="Referral options"
              className={className}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          }
        >
          <DotsThreeVerticalIcon />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="min-w-max"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {hasLostOption && (
            <DropdownMenuItem
              onClick={handleSelect(() => setConfirmAction("lost"))}
            >
              Mark as lost
            </DropdownMenuItem>
          )}
          {hasMoveToPaidOption && (
            <DropdownMenuItem onClick={handleSelect(() => onMoveToPaid?.())}>
              Move to paid
            </DropdownMenuItem>
          )}
          {onDelete && (
            <>
              {(hasLostOption || hasMoveToPaidOption) && (
                <DropdownMenuSeparator />
              )}
              <DropdownMenuItem
                variant="destructive"
                onClick={handleSelect(() => setConfirmAction("delete"))}
              >
                Delete referral
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ReferralConfirmationModal
        referral={referral}
        action={confirmAction}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
        onConfirm={() => {
          if (confirmAction === "lost") onLost?.();
          if (confirmAction === "delete") onDelete?.();
        }}
      />
    </>
  );
};
